import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import CopyButton from '../../components/ui/CopyButton.jsx';
import CopyRichButton from '../../components/ui/CopyRichButton.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { Pill } from '../../components/ui/index.jsx';
import { fmtDate } from '../../utils/format.js';

// A stamp is a badge made for ONE email: its link carries a code, and once a copy of that
// email reaches us through learn@, the stamp shows what the real email looked like. A
// copied badge in a fake email is then caught (core stamps.js).

export const LEARN = 'learn@check.summitshield.io';

const STATE = {
  unbound: ['Waiting for your copy', 'warn', 'Send the email with learn@ in Cc and this stamp will remember it.'],
  bound: ['In use', 'good', null],
  expired: ['Expired', 'neutral', null],
  revoked: ['Cancelled', 'bad', null],
};

/** Make a stamp. `events` (optional) adds an event picker, for the home page's quick button. */
export function NewStampModal({ eventId: fixedEvent, events, onClose, onMade }) {
  const [eventId, setEventId] = useState(fixedEvent || events?.[0]?.id || '');
  const [label, setLabel] = useState('');
  const [days, setDays] = useState('60');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const save = async () => {
    setBusy(true); setError(''); setErrors({});
    try {
      const { stamp } = await api(`/portal/events/${eventId}/stamps`, { method: 'POST', body: { label: label.trim() || null, expires_in_days: Number(days) } });
      onMade(stamp, eventId);
    } catch (err) {
      setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false);
    }
  };
  return (
    <Modal title="Make a stamp for one email" onClose={onClose} onSubmit={save} busy={busy}
      description="Use one stamp per email. Anyone who clicks it sees what that email looked like when you sent it."
      footer={<ModalActions submitLabel="Make stamp" busyLabel="Making…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      {events?.length > 1 && (
        <Field label="Which event it’s about" as="select" value={eventId} onChange={(e) => setEventId(Number(e.target.value))}
          hint="The stamp’s page shows this event’s name.">
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </Field>
      )}
      <Field label="What it’s for" optional value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120}
        placeholder="Exhibitor manual, 2 Nov" hint="Shown on the stamp’s public page, so people know which email it belongs to." />
      <Field label="Works for" as="select" value={days} onChange={(e) => setDays(e.target.value)} error={errors.expires_in_days}
        hint="After this, the stamp’s page says it has expired.">
        {[['14', '2 weeks'], ['30', '30 days'], ['60', '60 days'], ['90', '90 days'], ['180', '6 months'], ['365', 'A year']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Field>
    </Modal>
  );
}

/** Straight after making a stamp: the two things to do with it, in order. */
export function StampReadyModal({ stamp, eventId, onClose }) {
  return (
    <Modal title={`Stamp ${stamp.code} is ready`} onClose={onClose}
      description="Two steps and the email is protected. Anyone who clicks the badge sees what your real email looked like."
      footer={<button type="button" className="btn btn-primary" onClick={onClose}>Done</button>}>
      <ol className="stamp-ready">
        <li>
          <strong>Put the badge in your email</strong>
          <p className="muted small">Paste it at the bottom of the email it’s for, above or below your signature.</p>
          <div className="ev-copy-row">
            <CopyRichButton primary label="Copy for your email" html={stamp.html} text={`Verify this email: ${stamp.url}`} />
            <CopyButton text={stamp.html} label="Copy badge code" />
          </div>
        </li>
        <li>
          <strong>Send it with us in Cc</strong>
          <p className="muted small">Add <span className="mono">{LEARN}</span> to Cc (or Bcc). That’s how the stamp learns who really sent the email and where its links go.</p>
          <div className="ev-copy-row"><CopyButton text={LEARN} label="Copy address" /></div>
        </li>
      </ol>
      <p className="muted small">Sending it through a newsletter tool? <Link to={`/events/${eventId}`} onClick={onClose}>The event’s Share it tab</Link> can make each copy’s badge name its recipient.</p>
    </Modal>
  );
}

// Each newsletter tool's merge tag for the recipient's email address. The tool fills it in
// for every recipient, so each copy's badge names whose copy it is: /v/<code>/r/<address>.
// Only the link carries it, never the picture (that would track who opened the email).
const MERGE_TAGS = [
  ['mailchimp', 'Mailchimp', '*|EMAIL|*'],
  ['hubspot', 'HubSpot', '{{contact.email}}'],
  ['brevo', 'Brevo', '{{contact.EMAIL}}'],
  ['klaviyo', 'Klaviyo', '{{email}}'],
  ['campaign_monitor', 'Campaign Monitor', '[email]'],
  ['constant_contact', 'Constant Contact', '[[EmailAddress]]'],
  ['activecampaign', 'ActiveCampaign', '%EMAIL%'],
  ['other', 'Another tool', ''],
];

function PerRecipient({ stamp }) {
  const [tool, setTool] = useState('mailchimp');
  const [own, setOwn] = useState('');
  const tag = tool === 'other' ? own.trim() : MERGE_TAGS.find((t) => t[0] === tool)[2];
  const html = tag ? stamp.html.replace(`href="${stamp.url}"`, `href="${stamp.url}/r/${tag}"`) : '';
  return (
    <details className="stamp-recipient">
      <summary>Sending a newsletter? Make each copy’s badge name its recipient</summary>
      <p className="muted small">Your newsletter tool puts each recipient’s email address into their badge’s link. If a scammer copies the badge from their own copy, anyone who clicks it can see it wasn’t made for them, and you’ll see whose copy it came from.</p>
      <div className="stamp-recipient-row">
        <Field label="Your newsletter tool" as="select" value={tool} onChange={(e) => setTool(e.target.value)}>
          {MERGE_TAGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Field>
        {tool === 'other' && <Field label="Its merge tag for the email address" value={own} onChange={(e) => setOwn(e.target.value)} placeholder="e.g. {{email}}" maxLength={60} spellCheck={false} />}
      </div>
      {tag ? (
        <>
          <p className="muted small">Merge tag: <span className="mono">{tag}</span>. Send a test to yourself and check the badge link ends in your own address.</p>
          <div className="ev-copy-row"><CopyButton text={html} label="Copy newsletter badge code" /></div>
        </>
      ) : <p className="muted small">Type the merge tag your tool uses for the recipient’s email address.</p>}
    </details>
  );
}

function StampRow({ stamp, onRevoke }) {
  const [state, tone, note] = STATE[stamp.state] || STATE.unbound;
  const usable = ['unbound', 'bound'].includes(stamp.state);
  return (
    <li className="stamp-row">
      <div className="stamp-row-head">
        <span className="mono stamp-code">{stamp.code}</span>
        <Pill tone={tone}>{state}</Pill>
        {stamp.label && <strong>{stamp.label}</strong>}
      </div>
      <p className="muted small">
        {stamp.bound ? stamp.bound.summary : note}
        {' '}{stamp.state === 'revoked' ? `Cancelled ${fmtDate(stamp.revoked_at)}.` : stamp.state === 'expired' ? `Expired ${fmtDate(stamp.expires_at)}.` : `Works until ${fmtDate(stamp.expires_at)}.`}
        {stamp.bound?.message_id && <> <Link to={`/outbox/${stamp.bound.message_id}`}>See it in your Outbox</Link></>}
      </p>
      {usable && (
        <div className="ev-copy-row">
          <CopyRichButton primary label="Copy for your email" html={stamp.html} text={`Verify this email: ${stamp.url}`} />
          <CopyButton text={stamp.html} label="Copy badge code" />
          <a href={stamp.url} target="_blank" rel="noreferrer" className="btn btn-small btn-secondary">Open its page</a>
          <button type="button" className="link-button small" onClick={() => onRevoke(stamp)}>Cancel stamp</button>
        </div>
      )}
      {usable && <PerRecipient stamp={stamp} />}
    </li>
  );
}

export default function StampsSection({ event }) {
  const [stamps, setStamps] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);   // 'new' | { revoke: stamp }
  const [fresh, setFresh] = useState(null);

  const load = useCallback(() => {
    api(`/portal/events/${event.id}/stamps`).then((d) => { setStamps(d.stamps); setError(''); }).catch((err) => setError(err.message));
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const revoke = async (stamp) => {
    await api(`/portal/events/${event.id}/stamps/${stamp.id}/revoke`, { method: 'POST' });
    setModal(null); load();
  };
  const shown = (stamps || []).slice(0, 20);

  return (
    <section className="card" aria-labelledby="ev-stamps-h">
      <div className="stamp-head">
        <div>
          <h2 id="ev-stamps-h">Stamps for single emails</h2>
          <p className="muted">A badge anyone can copy proves nothing. A stamp belongs to one email: send that email with <span className="mono">{LEARN}</span> in Cc and the stamp remembers who sent it and where its links go. If a scammer copies it into a fake email, we tell people it doesn’t match.</p>
        </div>
        <div className="stamp-head-actions">
          <button type="button" className="btn btn-primary" onClick={() => setModal('new')}>Make a stamp</button>
          <a href="/stamp/" target="_blank" rel="noreferrer" className="btn btn-secondary btn-small">Get the desktop app</a>
        </div>
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      {fresh && (
        <Alert tone="success">
          Stamp {fresh.code} is ready. Copy it into the email it’s for, then send that email with {LEARN} in Cc.
        </Alert>
      )}
      {stamps && !stamps.length && <p className="muted small">No stamps yet. Make one for an important email, such as an invoice, a payment reminder or the exhibitor manual.</p>}
      {shown.length > 0 && <ul className="stamp-list">{shown.map((s) => <StampRow key={s.id} stamp={s} onRevoke={(x) => setModal({ revoke: x })} />)}</ul>}
      {stamps && stamps.length > shown.length && <p className="muted small">Showing your latest {shown.length} stamps.</p>}

      {modal === 'new' && <NewStampModal eventId={event.id} onClose={() => setModal(null)} onMade={(s) => { setModal(null); setFresh(s); load(); }} />}
      {modal?.revoke && (
        <ConfirmModal title={`Cancel stamp ${modal.revoke.code}?`} confirmLabel="Cancel stamp" cancelLabel="Keep it" danger onClose={() => setModal(null)} onConfirm={() => revoke(modal.revoke)}>
          <p>Its page will say it was cancelled, and any email carrying it will be treated with caution. Use this if the stamp was put in the wrong email, or someone is misusing it. It can’t be undone.</p>
        </ConfirmModal>
      )}
    </section>
  );
}
